import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
    Globe, ExternalLink, Copy, Check, Plus, Edit3, Trash2, 
    Eye, EyeOff, Link2, Settings, Palette, QrCode, Share2,
    GripVertical, Lock, Unlock, Calendar, Clock, Download
} from 'lucide-react';

const CustomerPublicPageManager = ({ customer, onClose, embedded = false }) => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({
        is_enabled: false,
        show_ci_section: true,
        show_links_section: true,
        header_background_url: '',
        custom_footer_text: '',
        password_protected: false,
        password: '',
        expires_at: ''
    });
    const [links, setLinks] = useState([]);
    const [publicUrl, setPublicUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [linkForm, setLinkForm] = useState({
        emoji: '🔗',
        name: '',
        url: '',
        description: '',
        is_active: true
    });

    const baseUrl = window.location.origin;

    useEffect(() => {
        fetchData();
    }, [customer.id]);

    const fetchData = async () => {
        try {
            const response = await api.get(`/customers/${customer.id}/public-page`);
            setSettings(response.data.settings);
            setLinks(response.data.links || []);
            setPublicUrl(`${baseUrl}/p/${customer.id}`);
        } catch (error) {
            console.error('Failed to fetch public page data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        try {
            await api.put(`/customers/${customer.id}/public-page`, settings);
            toast.success('Einstellungen gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(publicUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Link kopiert!');
    };

    const openPreview = () => {
        window.open(publicUrl, '_blank');
    };

    const openCreateLink = () => {
        setEditingLink(null);
        setLinkForm({ emoji: '🔗', name: '', url: '', description: '', is_active: true });
        setShowLinkModal(true);
    };

    const openEditLink = (link) => {
        setEditingLink(link);
        setLinkForm({
            emoji: link.emoji,
            name: link.name,
            url: link.url,
            description: link.description || '',
            is_active: link.is_active
        });
        setShowLinkModal(true);
    };

    const handleSaveLink = async () => {
        try {
            if (editingLink) {
                await api.put(`/customers/${customer.id}/public-page/links/${editingLink.id}`, linkForm);
                toast.success('Link aktualisiert');
            } else {
                await api.post(`/customers/${customer.id}/public-page/links`, linkForm);
                toast.success('Link erstellt');
            }
            setShowLinkModal(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
    };

    const handleDeleteLink = async (linkId) => {
        if (!window.confirm('Link wirklich löschen?')) return;
        try {
            await api.delete(`/customers/${customer.id}/public-page/links/${linkId}`);
            toast.success('Link gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const toggleLinkActive = async (link) => {
        try {
            await api.put(`/customers/${customer.id}/public-page/links/${link.id}`, {
                is_active: !link.is_active
            });
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    // Emoji picker options
    const emojiOptions = ['🔗', '📂', '📊', '🎥', '📝', '📸', '🚀', '💼', '📈', '🎨', '📅', '💡', '🌐', '📧', '📁', '🎯'];

    // Download QR Code as PNG
    const downloadQRCode = () => {
        const svg = document.getElementById('qr-code-svg');
        if (!svg) return;
        
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            canvas.width = 512;
            canvas.height = 512;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, 512, 512);
            
            const pngUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `${customer.name.replace(/\s+/g, '_')}_QR-Code.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            toast.success('QR-Code heruntergeladen');
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    // Check if link is expired
    const isExpired = settings.expires_at && new Date(settings.expires_at) < new Date();
    const expiresInDays = settings.expires_at ? Math.ceil((new Date(settings.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
                />
            </div>
        );
    }

    return (
        <div className={`space-y-4 ${embedded ? 'p-2 max-w-full overflow-hidden' : ''}`}>
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-semibold truncate">{customer.name} Dashboard</h2>
                    <p className="text-xs text-muted-foreground">Öffentliche Kundenseite verwalten</p>
                </div>
                {!embedded && <Button variant="outline" onClick={onClose} size="sm">Schließen</Button>}
            </div>

            {/* Quick Actions */}
            <Card className="shadow-soft overflow-hidden">
                <CardContent className="p-3">
                    <div className="space-y-2">
                        {/* Row 1: Toggle + Status */}
                        <div className="flex flex-wrap items-center gap-2">
                            {/* Enable Toggle - Auto saves */}
                            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-muted/50">
                                <Switch
                                    checked={settings.is_enabled}
                                    onCheckedChange={async (checked) => {
                                        const newSettings = { ...settings, is_enabled: checked };
                                        setSettings(newSettings);
                                        // Auto-save when toggling active state
                                        try {
                                            await api.put(`/customers/${customer.id}/public-page`, { is_enabled: checked });
                                            toast.success(checked ? 'Seite aktiviert' : 'Seite deaktiviert');
                                        } catch (error) {
                                            toast.error('Fehler beim Speichern');
                                            setSettings({ ...settings, is_enabled: !checked }); // Revert
                                        }
                                    }}
                                />
                                <span className="text-xs font-medium">
                                    {settings.is_enabled ? 'Aktiv' : 'Inaktiv'}
                                </span>
                            </div>

                            {/* Status Badges */}
                            {settings.password_protected && (
                                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                                    <Lock className="w-3 h-3" />
                                    Geschützt
                                </Badge>
                            )}
                            {settings.expires_at && (
                                <Badge variant={isExpired ? "destructive" : expiresInDays <= 7 ? "warning" : "secondary"} className="flex items-center gap-1 text-xs">
                                    <Clock className="w-3 h-3" />
                                    {isExpired ? 'Abgelaufen' : `${expiresInDays}d`}
                                </Badge>
                            )}
                        </div>

                        {/* Row 2: URL Display & Copy */}
                        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-muted/30 min-w-0">
                            <Link2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs truncate flex-1 font-mono">{publicUrl}</span>
                            <Button variant="ghost" size="sm" onClick={copyLink} className="h-6 w-6 p-0 shrink-0">
                                {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                            </Button>
                        </div>

                        {/* Row 3: Action Buttons */}
                        <div className="grid grid-cols-3 gap-1.5">
                            <Button variant="outline" onClick={() => setShowQRModal(true)} size="sm" className="h-8 text-xs px-2">
                                <QrCode className="w-3.5 h-3.5 mr-1 shrink-0" />
                                QR
                            </Button>

                            <Button variant="outline" onClick={openPreview} disabled={!settings.is_enabled || isExpired} size="sm" className="h-8 text-xs px-2">
                                <Eye className="w-3.5 h-3.5 mr-1 shrink-0" />
                                Vorschau
                            </Button>

                            <Button onClick={handleSaveSettings} size="sm" className="h-8 text-xs px-2">
                                Speichern
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className={`grid gap-4 ${embedded ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                {/* Settings */}
                <Card className="shadow-soft">
                    <CardHeader className="pb-2 pt-3 px-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Settings className="w-3.5 h-3.5" />
                            Einstellungen
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 px-3 pb-3">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <Label className="text-xs">CI-Bereich anzeigen</Label>
                                <p className="text-[10px] text-muted-foreground truncate">Farben & Logos</p>
                            </div>
                            <Switch
                                checked={settings.show_ci_section}
                                onCheckedChange={(checked) => setSettings({ ...settings, show_ci_section: checked })}
                                className="shrink-0"
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <Label className="text-xs">Links-Bereich anzeigen</Label>
                                <p className="text-[10px] text-muted-foreground truncate">Schnellzugriff Links</p>
                            </div>
                            <Switch
                                checked={settings.show_links_section}
                                onCheckedChange={(checked) => setSettings({ ...settings, show_links_section: checked })}
                                className="shrink-0"
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <Label className="text-xs flex items-center gap-1">
                                    {settings.password_protected ? <Lock className="w-2.5 h-2.5" /> : <Unlock className="w-2.5 h-2.5" />}
                                    Passwortschutz
                                </Label>
                                <p className="text-[10px] text-muted-foreground truncate">Zugang einschränken</p>
                            </div>
                            <Switch
                                checked={settings.password_protected}
                                onCheckedChange={(checked) => setSettings({ ...settings, password_protected: checked })}
                                className="shrink-0"
                            />
                        </div>

                        {settings.password_protected && (
                            <div>
                                <Label className="text-xs">Passwort</Label>
                                <Input
                                    type="password"
                                    value={settings.password || ''}
                                    onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                                    placeholder="Passwort eingeben"
                                    className="mt-1 h-8 text-xs"
                                />
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <Label className="text-xs flex items-center gap-1">
                                    <Calendar className="w-2.5 h-2.5" />
                                    Ablaufdatum
                                </Label>
                                <p className="text-[10px] text-muted-foreground truncate">Link automatisch deaktivieren</p>
                            </div>
                            <Switch
                                checked={!!settings.expires_at}
                                onCheckedChange={(checked) => {
                                    if (checked) {
                                        // Default: 30 days from now
                                        const defaultExpiry = new Date();
                                        defaultExpiry.setDate(defaultExpiry.getDate() + 30);
                                        setSettings({ ...settings, expires_at: defaultExpiry.toISOString().split('T')[0] });
                                    } else {
                                        setSettings({ ...settings, expires_at: '' });
                                    }
                                }}
                                className="shrink-0"
                            />
                        </div>

                        {settings.expires_at && (
                            <div>
                                <Label className="text-xs">Gültig bis</Label>
                                <Input
                                    type="date"
                                    value={settings.expires_at ? settings.expires_at.split('T')[0] : ''}
                                    onChange={(e) => setSettings({ ...settings, expires_at: e.target.value })}
                                    className="mt-1 h-8 text-xs"
                                    min={new Date().toISOString().split('T')[0]}
                                />
                                {isExpired && (
                                    <p className="text-[10px] text-destructive mt-1">⚠️ Link ist abgelaufen!</p>
                                )}
                            </div>
                        )}

                        <div>
                            <Label className="text-xs">Hintergrundbild URL</Label>
                            <Input
                                value={settings.header_background_url || ''}
                                onChange={(e) => setSettings({ ...settings, header_background_url: e.target.value })}
                                placeholder="https://..."
                                className="mt-1 h-8 text-xs"
                            />
                        </div>

                        <div>
                            <Label className="text-xs">Custom Footer Text</Label>
                            <Textarea
                                value={settings.custom_footer_text || ''}
                                onChange={(e) => setSettings({ ...settings, custom_footer_text: e.target.value })}
                                placeholder="© 2025 Firmenname..."
                                className="mt-1 text-xs"
                                rows={2}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Links */}
                <Card className="shadow-soft">
                    <CardHeader className="pb-2 pt-3 px-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Link2 className="w-3.5 h-3.5" />
                            Links ({links.length})
                        </CardTitle>
                        <Button size="sm" onClick={openCreateLink} className="h-7 text-xs">
                            <Plus className="w-3 h-3 mr-1" />
                            Neu
                        </Button>
                    </CardHeader>
                    <CardContent className="px-3 pb-3">
                        {links.length === 0 ? (
                            <div className="text-center py-4 text-muted-foreground">
                                <Link2 className="w-6 h-6 mx-auto mb-1 opacity-50" />
                                <p className="text-xs">Noch keine Links</p>
                                <Button variant="link" size="sm" onClick={openCreateLink} className="h-6 text-xs">
                                    Ersten Link erstellen
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {links.map((link, index) => (
                                    <motion.div
                                        key={link.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                            link.is_active ? 'bg-muted/30 hover:bg-muted/50' : 'bg-muted/10 opacity-60'
                                        }`}
                                    >
                                        <span className="text-base shrink-0">{link.emoji}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium truncate">{link.name}</p>
                                            <p className="text-[10px] text-muted-foreground truncate">{link.url}</p>
                                        </div>
                                        <div className="flex items-center shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => toggleLinkActive(link)}
                                                className="h-6 w-6 p-0"
                                            >
                                                {link.is_active ? (
                                                    <Eye className="w-3 h-3" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3" />
                                                )}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditLink(link)}
                                                className="h-6 w-6 p-0"
                                            >
                                                <Edit3 className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteLink(link.id)}
                                                className="text-destructive hover:text-destructive h-6 w-6 p-0"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Link Modal */}
            <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingLink ? 'Link bearbeiten' : 'Neuer Link'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Emoji</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {emojiOptions.map((emoji) => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => setLinkForm({ ...linkForm, emoji })}
                                        className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                                            linkForm.emoji === emoji 
                                                ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2' 
                                                : 'bg-muted/50 hover:bg-muted'
                                        }`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label>Name</Label>
                            <Input
                                value={linkForm.name}
                                onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
                                placeholder="z.B. Google Drive"
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label>URL</Label>
                            <Input
                                value={linkForm.url}
                                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                                placeholder="https://..."
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label>Beschreibung (optional)</Label>
                            <Textarea
                                value={linkForm.description}
                                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                                placeholder="Kurze Beschreibung..."
                                className="mt-1"
                                rows={2}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <Label>Aktiv</Label>
                            <Switch
                                checked={linkForm.is_active}
                                onCheckedChange={(checked) => setLinkForm({ ...linkForm, is_active: checked })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSaveLink} disabled={!linkForm.name || !linkForm.url}>
                            {editingLink ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* QR Code Modal */}
            <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <QrCode className="w-5 h-5" />
                            QR-Code
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-4 py-4">
                        <div className="p-4 bg-white rounded-xl shadow-inner">
                            <QRCodeSVG
                                id="qr-code-svg"
                                value={publicUrl}
                                size={200}
                                level="H"
                                includeMargin={true}
                            />
                        </div>
                        <p className="text-sm text-center text-muted-foreground">
                            Scannen Sie diesen Code, um direkt zur Kundenseite zu gelangen.
                        </p>
                        <div className="flex gap-2 w-full">
                            <Button variant="outline" className="flex-1" onClick={copyLink}>
                                <Copy className="w-4 h-4 mr-2" />
                                Link kopieren
                            </Button>
                            <Button className="flex-1" onClick={downloadQRCode}>
                                <Download className="w-4 h-4 mr-2" />
                                Herunterladen
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CustomerPublicPageManager;
